import {
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { TranslocoService } from "@ngneat/transloco";
import Quill from "quill2";

import {
  createAdditionalNotesQuill,
  normalizeQuillEditorHtml,
  setQuillHtml,
} from "../../../core/ult-functions/additional-notes-quill2";

@Component({
  selector: "app-additional-notes-rich-text-editor",
  templateUrl: "./additional-notes-rich-text-editor.component.html",
  styleUrls: ["./additional-notes-rich-text-editor.component.scss"],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AdditionalNotesRichTextEditorComponent),
      multi: true,
    },
  ],
})
export class AdditionalNotesRichTextEditorComponent
  implements ControlValueAccessor, AfterViewInit, OnDestroy
{
  @ViewChild("editor") editorRef?: ElementRef<HTMLDivElement>;
  @ViewChild("toolbarHost") toolbarHostRef?: ElementRef<HTMLDivElement>;
  @Input() placeholder = "";

  private quill?: Quill;
  private pendingValue: string | null = null;
  private lastEmittedValue = "";
  private disabled = false;
  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private readonly onEditorKeyDown = (event: KeyboardEvent): void => {
    if (!(event.ctrlKey || event.metaKey)) {
      return;
    }

    const key = event.key.toLowerCase();
    if (key === "z") {
      event.preventDefault();
      if (event.shiftKey) {
        this.quill?.history.redo();
      } else {
        this.quill?.history.undo();
      }
      return;
    }

    if (key === "y") {
      event.preventDefault();
      this.quill?.history.redo();
    }
  };

  constructor(private translocoService: TranslocoService) {}

  ngAfterViewInit(): void {
    const editor = this.editorRef?.nativeElement;
    const toolbarHost = this.toolbarHostRef?.nativeElement;
    if (!editor || !toolbarHost || this.quill) {
      return;
    }

    this.quill = createAdditionalNotesQuill(
      editor,
      toolbarHost,
      this.placeholder,
      {
        table: this.translocoService.translate(
          "itineraries.addEditItinerary.additionalNotes.insertTable",
        ),
        insertRowAbove: this.translocoService.translate(
          "itineraries.addEditItinerary.additionalNotes.insertRowAbove",
        ),
        insertRowBelow: this.translocoService.translate(
          "itineraries.addEditItinerary.additionalNotes.insertRowBelow",
        ),
        insertColumnLeft: this.translocoService.translate(
          "itineraries.addEditItinerary.additionalNotes.insertColumnLeft",
        ),
        insertColumnRight: this.translocoService.translate(
          "itineraries.addEditItinerary.additionalNotes.insertColumnRight",
        ),
        deleteRow: this.translocoService.translate(
          "itineraries.addEditItinerary.additionalNotes.deleteRow",
        ),
        deleteColumn: this.translocoService.translate(
          "itineraries.addEditItinerary.additionalNotes.deleteColumn",
        ),
      },
    );

    this.quill.root.addEventListener("keydown", this.onEditorKeyDown);

    this.quill.on("text-change", (_delta, _oldDelta, source) => {
      if (source !== "user") {
        return;
      }

      const html = normalizeQuillEditorHtml(this.quill?.root.innerHTML || "");
      this.lastEmittedValue = html;
      this.onChange(html);
    });

    this.quill.on("selection-change", (range) => {
      if (!range) {
        this.onTouched();
      }
    });

    if (this.disabled) {
      this.quill.enable(false);
    }

    if (this.pendingValue !== null) {
      setQuillHtml(this.quill, this.pendingValue);
      this.lastEmittedValue = normalizeQuillEditorHtml(this.pendingValue);
      this.pendingValue = null;
    }
  }

  ngOnDestroy(): void {
    this.quill?.root.removeEventListener("keydown", this.onEditorKeyDown);

    const toolbarHost = this.toolbarHostRef?.nativeElement;
    if (toolbarHost) {
      toolbarHost.innerHTML = "";
    }

    const editor = this.editorRef?.nativeElement;
    if (editor) {
      editor.innerHTML = "";
      editor.className = "additional-notes-rich-text-editor__editor-host";
    }

    this.quill = undefined;
  }

  writeValue(value: string | null): void {
    const normalized = normalizeQuillEditorHtml(value || "");

    if (normalized === this.lastEmittedValue) {
      return;
    }

    this.lastEmittedValue = normalized;

    if (!this.quill) {
      this.pendingValue = normalized;
      return;
    }

    setQuillHtml(this.quill, normalized);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.quill?.enable(!isDisabled);
  }
}
